import { useTranslation } from "react-i18next";
import { useState } from "react";
import { X, CalendarClock, Clock } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";

/** `useLocale().formatDate` — passed in so these stay pure. */
type DateFormatter = (date: Date, pattern: string) => string;

interface ScheduledMaintenanceBannerProps {
  scheduledStartAt: string;
  scheduledEndAt: string;
  message: string | null;
}

function fmtDate(iso: string, fmt: DateFormatter): string {
  return fmt(new Date(iso), "dd MMM yyyy");
}

function fmtTime(iso: string, fmt: DateFormatter): string {
  return fmt(new Date(iso), "hh:mm a");
}

function fmtShort(iso: string, fmt: DateFormatter): string {
  return fmt(new Date(iso), "dd MMM, hh:mm a");
}

export function ScheduledMaintenanceBanner({
  scheduledStartAt,
  scheduledEndAt,
  message,
}: ScheduledMaintenanceBannerProps) {
  const { t } = useTranslation("common");
  const { formatDate } = useLocale();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const sameDay =
    new Date(scheduledStartAt).toDateString() ===
    new Date(scheduledEndAt).toDateString();

  const dateLabel = sameDay
    ? `${fmtDate(scheduledStartAt, formatDate)},  ${fmtTime(scheduledStartAt, formatDate)} – ${fmtTime(scheduledEndAt, formatDate)}`
    : `${fmtDate(scheduledStartAt, formatDate)} ${fmtTime(scheduledStartAt, formatDate)} – ${fmtDate(scheduledEndAt, formatDate)} ${fmtTime(scheduledEndAt, formatDate)}`;

  return (
    <div className="sticky top-0 z-99999 bg-gradient-subscription-active font-sans select-none">
      {/* shimmer top */}
      <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-white/40 to-transparent pointer-events-none" />
      {/* shadow bottom */}
      <div className="absolute inset-x-0 bottom-0 h-px bg-black/10 pointer-events-none" />

      <div className="relative flex items-center px-3 sm:px-4 md:px-6 py-2 sm:py-2.5">
        {/* ── Mobile layout (< sm): single line ── */}
        <div
          className={`flex sm:hidden flex-1 items-center gap-2 min-w-0 pe-7 ${
            message ? "" : "justify-center"
          }`}
        >
          {/* icon */}
          <div className="shrink-0 flex items-center justify-center size-7 rounded-lg bg-white/15 border border-white/20">
            <CalendarClock className="size-3.5 text-white" strokeWidth={2} />
          </div>
          {/* pulse + label */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="relative flex size-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
              <span className="relative inline-flex size-1.5 rounded-full bg-white" />
            </span>
            <span className="text-[10px] font-extrabold text-white uppercase tracking-widest whitespace-nowrap">
              {t("common:maintenance.maintenance")}
            </span>
          </div>
          {/* message — fills remaining space */}
          {message && (
            <p className="text-[10px] text-white/85 font-medium flex-1 truncate min-w-0">
              {message}
            </p>
          )}
          {/* date chip */}
          <div className="flex items-center gap-1 shrink-0 bg-white/10 border border-white/15 rounded-md px-2 py-0.5">
            <span className="text-[10px] font-semibold text-white/90 whitespace-nowrap">
              {fmtShort(scheduledStartAt, formatDate)} –{" "}
              {fmtTime(scheduledEndAt, formatDate)}
            </span>
          </div>
        </div>

        {/* ── Desktop layout (sm+): full-width header ── */}
        <div className="hidden sm:flex flex-1 items-center gap-3 min-w-0 pe-10">
          {/* icon badge */}
          <div className="shrink-0 flex items-center justify-center size-8 rounded-lg bg-white/15 border border-white/20">
            <CalendarClock className="size-4 text-white" strokeWidth={2} />
          </div>

          {/* pulse + label */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="relative flex size-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
              <span className="relative inline-flex size-1.5 rounded-full bg-white" />
            </span>
            <span className="text-[11px] font-extrabold text-white uppercase tracking-[0.12em] whitespace-nowrap">
              {t("common:maintenance.scheduled_maintenance")}
            </span>
          </div>

          {/* divider */}
          <span className="w-px h-3.5 bg-white/25 shrink-0" />

          {/* message — fills remaining space */}
          {message && (
            <p className="text-xs text-white/85 font-medium flex-1 truncate">
              {message}
            </p>
          )}

          {/* divider before date */}
          {message && <span className="w-px h-3.5 bg-white/25 shrink-0" />}

          {/* date chip */}
          <div className="flex items-center gap-1.5 shrink-0 bg-white/10 border border-white/15 rounded-md px-2.5 py-1">
            <Clock className="size-3 text-white/70" strokeWidth={2} />
            <span className="text-[11px] font-semibold text-white/90 whitespace-nowrap">
              {dateLabel}
            </span>
          </div>
        </div>

        {/* ── Dismiss ── */}
        <button
          onClick={() => setDismissed(true)}
          aria-label={t("ui_kit.dismiss")}
          className="absolute end-3 sm:end-4 md:end-6 flex items-center justify-center size-6 rounded-md text-white/50 hover:text-white hover:bg-white/20 active:bg-white/30 transition-all duration-150"
        >
          <X className="size-3.5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
